import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Space, Button, message, Modal, Form, Input, Select, Switch, ColorPicker, InputNumber, Popconfirm, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, UserAddOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { shiftRotationsApi, employeeShiftsApi, departmentsApi, employeesApi, attendanceApi, attendanceSettingsApi, show422Error, ValidationError } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { BulkSelect } from '../../common/bulkSelectProps';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

const { Option } = Select;

const ShiftRotationTab = ({ data, onUpdate }) => {
  const { canEdit, canDelete } = useResourcePermissions('shift');
  const [loading, setLoading] = useState(false);
  const [rotations, setRotations] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRotation, setEditingRotation] = useState(null);
  const [rotationData, setRotationData] = useState(null);
  const [shifts, setShifts] = useState([]); // برای ترتیب چرخش شیفت‌ها (از employee-shifts)
  const [rotationShifts, setRotationShifts] = useState([]); // برای فیلد «انتخاب شیفت» (از attendance-settings?tab=shift)
  const [dates, setDates] = useState([]);
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [rotationFrequency, setRotationFrequency] = useState('weekly');
  const [automateModalVisible, setAutomateModalVisible] = useState(false);
  const [automateForm] = Form.useForm();
  const [automateLoading, setAutomateLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (data) {
      setRotations(Array.isArray(data) ? data : []);
    }
  }, [data]);

  useEffect(() => {
    loadShifts();
    loadDepartments();
    loadRotationShifts();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await departmentsApi.getAll();
      const deptsList = res.data || [];
      setDepartments(Array.isArray(deptsList) ? deptsList : []);
    } catch (error) {
      console.error('خطا در دریافت لیست دپارتمان‌ها:', error);
    }
  };

  const loadEmployeesByDepartment = async (departmentId) => {
    try {
      const res = await attendanceApi.getEmployeesByDepartment(departmentId);
      const employeesList = res.data?.employees || res?.employees || [];
      setEmployees(Array.isArray(employeesList) ? employeesList : []);
    } catch (error) {
      console.error('خطا در دریافت لیست کارمندان:', error);
      message.error('خطا در دریافت لیست کارمندان');
    }
  };

  const loadAllEmployees = async () => {
    try {
      const res = await employeesApi.getAll();
      const employeesList = res.data || [];
      setEmployees(Array.isArray(employeesList) ? employeesList : []);
    } catch (error) {
      console.error('خطا در دریافت لیست کارمندان:', error);
      message.error('خطا در دریافت لیست کارمندان');
    }
  };

  // شیفت‌ها برای ترتیب چرخش (از employee-shifts)
  const loadShifts = async () => {
    try {
      const res = await employeeShiftsApi.getAll();
      const shiftsList = res.data?.shifts || res.data || [];
      setShifts(Array.isArray(shiftsList) ? shiftsList : []);
    } catch (error) {
      console.error('خطا در دریافت لیست شیفت‌ها:', error);
    }
  };

  // شیفت‌ها برای فیلد «انتخاب شیفت» (از attendance-settings?tab=shift)
  const loadRotationShifts = async () => {
    try {
      const res = await attendanceSettingsApi.getSettings('shift');
      const responseData = res.data || res;
      const data = responseData?.data || responseData || {};
      const shiftsList = data.employee_shifts || [];
      setRotationShifts(Array.isArray(shiftsList) ? shiftsList : []);
    } catch (error) {
      console.error('خطا در دریافت لیست شیفت‌های تنظیمات حضور و غیاب:', error);
    }
  };

  const loadRotationData = async (id) => {
    try {
      setFormLoading(true);
      const res = await shiftRotationsApi.getById(id);
      const rotationData = res.data || res;
      setRotationData(rotationData);
      
      const rotation = rotationData.shift_rotation || {};
      const shiftsList = rotationData.employee_shifts || [];
      const datesList = rotationData.dates || [];
      
      setShifts(shiftsList);
      setDates(datesList);

      // استخراج «ترتیب چرخش شیفت‌ها» از sequences (به ترتیب sequence)
      const apiSequences = Array.isArray(rotation.sequences) ? rotation.sequences : [];
      const sortedApiSequences = apiSequences
        .slice()
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
      
      const rotationSequenceFromApi = sortedApiSequences
        .map((s) => s.employee_shift_id)
        .filter((v) => v !== undefined && v !== null);

      form.setFieldsValue({
        rotation_name: rotation.rotation_name,
        rotation_frequency: rotation.rotation_frequency || 'weekly',
        schedule_on: rotation.schedule_on || null,
        rotation_date: rotation.rotation_date || null,
        color_code: rotation.color_code || '#16813D',
        // در پاسخ API فیلدی به نام rotation_sequence نداریم؛ از sequences استفاده می‌کنیم
        rotation_sequence: rotationSequenceFromApi,
        // پر کردن فیلد «انتخاب شیفت» از shift موجود در API (اولین آیتم sequence)
        rotation_shift:
          sortedApiSequences?.[0]?.shift?.id ??
          sortedApiSequences?.[0]?.employee_shift_id ??
          null,
        override_shift: rotation.override_shift === 'yes',
        send_mail: rotation.send_mail === 'yes',
      });

      setRotationFrequency(rotation.rotation_frequency || 'weekly');
    } catch (error) {
      console.error('خطا در دریافت اطلاعات گردش:', error);
      message.error('خطا در دریافت اطلاعات گردش');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRotation(null);
    setRotationData(null);
    setRotationFrequency('weekly');
    form.resetFields();
    form.setFieldsValue({
      rotation_frequency: 'weekly',
      color_code: '#16813D',
      override_shift: true,
      send_mail: true,
    });
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingRotation(record);
    setModalVisible(true);
    await loadRotationData(record.id);
  };

  const handleDelete = async (id) => {
    try {
      await shiftRotationsApi.delete(id);
      message.success('گردش شیفت با موفقیت حذف شد');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('خطا در حذف گردش:', error);
      message.error(error?.message || 'خطا در حذف گردش شیفت');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setFormLoading(true);

      // ترتیب چرخش شیفت‌ها از روی انتخاب کاربر
      const selectedSequence = values.rotation_sequence || [];
      const sortOrder = Array.isArray(selectedSequence)
        ? selectedSequence.map((_, index) => index + 1)
        : [];
      const shiftsList = Array.isArray(selectedSequence) ? selectedSequence : [];

      const payload = {
        rotation_name: values.rotation_name,
        rotation_frequency: values.rotation_frequency,
        schedule_on: values.rotation_frequency === 'monthly' ? null : values.schedule_on,
        rotation_date: values.rotation_frequency === 'monthly' ? values.rotation_date : null,
        color_code: typeof values.color_code === 'string' ? values.color_code : values.color_code?.toHexString() || '#16813D',
        rotation_sequence: values.rotation_sequence || null,
        override_shift: values.override_shift ? 'yes' : 'no',
        send_mail: values.send_mail ? 'yes' : 'no',
        sort_order: sortOrder,
        shifts: shiftsList,
      };

      if (editingRotation) {
        await shiftRotationsApi.update(editingRotation.id, {
          ...payload,
          rotation_id: editingRotation.id,
        });
        message.success('گردش شیفت با موفقیت ویرایش شد');
      } else {
        await shiftRotationsApi.create(payload);
        message.success('گردش شیفت با موفقیت ایجاد شد');
      }

      setModalVisible(false);
      setEditingRotation(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره گردش شیفت');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleChangeStatus = async (id, status) => {
    try {
      await shiftRotationsApi.changeStatus({ id, status });
      message.success(`وضعیت گردش با موفقیت به ${status === 'active' ? 'فعال' : 'غیرفعال'} تغییر یافت`);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('خطا در تغییر وضعیت:', error);
      message.error(error?.message || 'خطا در تغییر وضعیت گردش');
    }
  };

  const handleRunRotation = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک گردش را انتخاب کنید');
      return;
    }

    try {
      setLoading(true);
      await shiftRotationsApi.runRotation({ rotation_ids: selectedRowKeys });
      message.success('گردش‌های انتخاب شده با موفقیت اجرا شدند');
      setSelectedRowKeys([]);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('خطا در اجرای گردش:', error);
      message.error(error?.message || 'خطا در اجرای گردش‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAutomateModal = () => {
    setAutomateModalVisible(true);
    automateForm.resetFields();
    // بارگذاری لیست همه کارمندان (ایندکس کارمندان)
    loadAllEmployees();
  };

  const handleAutomateSubmit = async () => {
    try {
      const values = await automateForm.validateFields();
      setAutomateLoading(true);

      const payload = {
        department_id: values.department_id || null,
        user_id: values.user_id || [],
        rotation: values.rotation,
      };

      await shiftRotationsApi.storeAutomateShift(payload);
      message.success('گردش با موفقیت به کارمندان اختصاص داده شد');
      setAutomateModalVisible(false);
      automateForm.resetFields();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(automateForm);
      } else if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در اختصاص گردش');
      }
    } finally {
      setAutomateLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const columns = [
    {
      title: 'نام گردش',
      dataIndex: 'rotation_name',
      key: 'rotation_name',
      render: (text, record) => (
        <div className="flex items-center gap-2">
          {record.color_code && (
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: record.color_code }}
            />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'فرکانس',
      dataIndex: 'rotation_frequency',
      key: 'rotation_frequency',
      render: (text) => {
        const frequencyMap = {
          weekly: 'هفتگی',
          'bi-weekly': 'دو هفته‌ای',
          monthly: 'ماهانه',
        };
        return (
          <Tag color={text === 'weekly' ? 'blue' : text === 'monthly' ? 'green' : 'orange'}>
            {frequencyMap[text] || text}
          </Tag>
        );
      },
    },
    {
      title: 'زمان برنامه‌ریزی',
      key: 'schedule',
      render: (_, record) => {
        if (record.rotation_frequency === 'weekly' || record.rotation_frequency === 'bi-weekly') {
          const scheduleMap = {
            'every-saturday': 'هر شنبه',
            'every-sunday': 'هر یکشنبه',
            'every-monday': 'هر دوشنبه',
            'every-tuesday': 'هر سه‌شنبه',
            'every-wednesday': 'هر چهارشنبه',
            'every-thursday': 'هر پنج‌شنبه',
            'every-friday': 'هر جمعه',
          };
          return record.schedule_on ? (
            <span>{scheduleMap[record.schedule_on] || record.schedule_on}</span>
          ) : '-';
        } else {
          return record.rotation_date ? `روز ${record.rotation_date} هر ماه` : '-';
        }
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (text) => (
        <Tag color={text === 'active' ? 'green' : 'default'}>
          {text === 'active' ? 'فعال' : 'غیرفعال'}
        </Tag>
      ),
    },
    {
      title: 'فعال / غیرفعال',
      key: 'toggle_status',
      align: 'center',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.status === 'active'}
          checkedChildren="فعال"
          unCheckedChildren="غیرفعال"
          onChange={(checked) =>
            handleChangeStatus(record.id, checked ? 'active' : 'inactive')
          }
        />
      ),
    },
    {
      title: 'تعداد توالی',
      key: 'sequences_count',
      render: (_, record) => record.sequences?.length || 0,
    },
    {
      title: 'توالی شیفت‌ها',
      key: 'sequences',
      render: (_, record) => {
        if (!record.sequences || record.sequences.length === 0) return '-';
        
        const sortedSequences = [...record.sequences].sort((a, b) => a.sequence - b.sequence);
        
        return (
          <Space wrap>
            {sortedSequences.map((seq, index) => (
              <Tag key={seq.id} color="blue">
                {index + 1}. {seq.shift?.shift_name || '-'} ({seq.shift?.shift_short_code || '-'})
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space>
          {canEdit ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            >
              
            </Button>
          ) : null}
          {canDelete ? (
            <Popconfirm
              title="آیا مطمئن هستید؟"
              onConfirm={() => handleDelete(record.id)}
              okText="بله"
              cancelText="خیر"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
                title="حذف"
              >
                
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card className="bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'CustomFont' }}>
            لیست گردش شیفت‌ها
          </h3>
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button
                type="default"
                icon={<PlayCircleOutlined />}
                onClick={handleRunRotation}
                loading={loading}
                style={{ fontFamily: 'CustomFont' }}
              >
                اجرای گردش‌های انتخاب شده ({selectedRowKeys.length})
              </Button>
            )}
            <Button
              type="default"
              icon={<UserAddOutlined />}
              onClick={handleOpenAutomateModal}
              style={{ fontFamily: 'CustomFont' }}
            >
              اختصاص خودکار
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              className="bg-green-600 hover:bg-green-700 border-0"
              style={{ fontFamily: 'CustomFont' }}
            >
              افزودن گردش جدید
            </Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={rotations}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `مجموع ${total} گردش`,
          }}
          style={{ fontFamily: 'CustomFont' }}
        />
      </Card>

      {/* Modal ایجاد/ویرایش گردش شیفت */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {editingRotation ? 'ویرایش گردش شیفت' : 'افزودن گردش شیفت جدید'}
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRotation(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
        style={{ fontFamily: 'CustomFont' }}
      >
        {formLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
              در حال بارگذاری...
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
          >
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>نام گردش</span>}
                name="rotation_name"
                rules={[{ required: true, message: 'لطفاً نام گردش را وارد کنید' }]}
              >
                <Input placeholder="مثال: چرخشی 1" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>تنظیم تناوب چرخش</span>}
                name="rotation_frequency"
                rules={[{ required: true, message: 'لطفاً فرکانس را انتخاب کنید' }]}
              >
                <Select onChange={(value) => setRotationFrequency(value)}>
                  <Option value="weekly">هفتگی</Option>
                  <Option value="bi-weekly">دو هفته‌ای</Option>
                  <Option value="monthly">ماهانه</Option>
                </Select>
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {rotationFrequency === 'monthly' ? (
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>روز ماه</span>}
                  name="rotation_date"
                  rules={[{ required: true, message: 'لطفاً روز ماه را انتخاب کنید' }]}
                >
                  <Select placeholder="انتخاب روز ماه">
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                      <Option key={day} value={day}>
                        روز {day}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>زمان برنامه‌ریزی</span>}
                  name="schedule_on"
                  rules={[{ required: true, message: 'لطفاً زمان برنامه‌ریزی را انتخاب کنید' }]}
                >
                  <Select placeholder="انتخاب زمان">
                    <Option value="every-saturday">هر شنبه</Option>
                    <Option value="every-sunday">هر یکشنبه</Option>
                    <Option value="every-monday">هر دوشنبه</Option>
                    <Option value="every-tuesday">هر سه‌شنبه</Option>
                    <Option value="every-wednesday">هر چهارشنبه</Option>
                    <Option value="every-thursday">هر پنج‌شنبه</Option>
                    <Option value="every-friday">هر جمعه</Option>
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>رنگ</span>}
                name="color_code"
                rules={[{ required: true, message: 'لطفاً رنگ را انتخاب کنید' }]}
              >
                <ColorPicker showText format="hex" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>انتخاب شیفت</span>}
                name="rotation_shift"
              >
                <Select placeholder="انتخاب شیفت (اختیاری)">
                  {rotationShifts.map((shift) => (
                    <Option key={shift.id} value={shift.id}>
                      <div className="flex items-center gap-2">
                        {shift.color && (
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: shift.color }}
                          />
                        )}
                        <span>{shift.shift_name} ({shift.shift_short_code})</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>جایگزینی شیفت</span>}
                name="override_shift"
                valuePropName="checked"
              >
                <Switch checkedChildren="بله" unCheckedChildren="خیر" />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>ارسال ایمیل</span>}
                name="send_mail"
                valuePropName="checked"
              >
                <Switch checkedChildren="بله" unCheckedChildren="خیر" />
              </Form.Item>
            </div>

            {/* ترتیب چرخش شیفت‌ها */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>ترتیب چرخش شیفت‌ها</span>}
                name="rotation_sequence"
              >
                    <BulkSelect
                  mode="multiple"
                  placeholder="انتخاب ترتیب چرخش شیفت‌ها"
                  style={{ width: '100%' }}
                  allValues={shifts.map((s) => s.id)}
                    >
                      {shifts.map((shift) => (
                        <Option key={shift.id} value={shift.id}>
                          <div className="flex items-center gap-2">
                            {shift.color && (
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: shift.color }}
                              />
                            )}
                            <span>{shift.shift_name} ({shift.shift_short_code})</span>
                          </div>
                        </Option>
                      ))}
                    </BulkSelect>
              </Form.Item>
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingRotation(null);
                  form.resetFields();
                }}
                disabled={formLoading}
                style={{ fontFamily: 'CustomFont' }}
              >
                لغو
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={formLoading}
                className="bg-green-600 hover:bg-green-700 border-0"
                style={{ fontFamily: 'CustomFont' }}
              >
                {editingRotation ? 'ویرایش' : 'ذخیره'}
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* Modal اختصاص خودکار گردش */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            افزودن شیفت خودکار
          </span>
        }
        open={automateModalVisible}
        onCancel={() => {
          setAutomateModalVisible(false);
          automateForm.resetFields();
          setEmployees([]);
        }}
        footer={null}
        width={600}
        style={{ fontFamily: 'CustomFont' }}
      >
        <Form
          form={automateForm}
          layout="vertical"
          onFinish={handleAutomateSubmit}
          style={{ fontFamily: 'CustomFont' }}
        >
          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>گردش</span>}
            name="rotation"
            rules={[{ required: true, message: 'لطفاً گردش را انتخاب کنید' }]}
          >
            <Select placeholder="انتخاب گردش">
              {rotations.map((rotation) => (
                <Option key={rotation.id} value={rotation.id}>
                  <div className="flex items-center gap-2">
                    {rotation.color_code && (
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: rotation.color_code }}
                      />
                    )}
                    <span>{rotation.rotation_name}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>دپارتمان</span>}
            name="department_id"
          >
            <Select
              placeholder="انتخاب دپارتمان (اختیاری)"
              allowClear
              onChange={(value) => {
                if (value) {
                  loadEmployeesByDepartment(value);
                } else {
                  // در صورت حذف دپارتمان، مجدداً همه کارمندان را بارگذاری کن
                  loadAllEmployees();
                  automateForm.setFieldsValue({ user_id: [] });
                }
              }}
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.team_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>کارمندان</span>}
            name="user_id"
            rules={[{ required: true, message: 'لطفاً حداقل یک کارمند را انتخاب کنید' }]}
          >
            <BulkSelect
              mode="multiple"
              placeholder="انتخاب کارمندان"
              showSearch
              allValues={employees.map((e) => e.id)}
              filterOption={(input, option) =>
                (option?.children?.props?.children || option?.children || '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {employees.map((emp) => (
                <Option key={emp.id} value={emp.id}>
                  {emp.name} {emp.email ? `(${emp.email})` : ''}
                </Option>
              ))}
            </BulkSelect>
          </Form.Item>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
            <Button
              onClick={() => {
                setAutomateModalVisible(false);
                automateForm.resetFields();
                setEmployees([]);
              }}
              disabled={automateLoading}
              style={{ fontFamily: 'CustomFont' }}
            >
              لغو
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={automateLoading}
              className="bg-green-600 hover:bg-green-700 border-0"
              style={{ fontFamily: 'CustomFont' }}
            >
              ذخیره
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ShiftRotationTab;

